import { createSelectSchema } from 'drizzle-zod';
import { z } from 'zod';
import {
  activityTypeTable,
  contractTable,
  dbActionTable,
  logBestTable,
  logTable,
  messageTable,
  nutritionTable,
  nutritionTypeTable,
  photoTable,
  postTable,
  profileTable,
  recordingTable,
  teamMemberTable,
  teamShareTable,
  teamTable,
  userTable,
  videoTable,
} from '~/db/schema.sql';

export const DbActionSchema = createSelectSchema(dbActionTable, {
  type: z.enum(['insert', 'update', 'delete']),
  uniqueColumn: z.enum(['id', 'userId', 'nameId']),
  data: z.any(),
});

export type DbAction = z.infer<typeof DbActionSchema>;

export const ContractSchema = createSelectSchema(contractTable, {
  data: z.any(),
});

export type Contract = z.infer<typeof ContractSchema>;

export const PhotoSchema = createSelectSchema(photoTable);

export type Photo = z.infer<typeof PhotoSchema>;

export const VideoSchema = createSelectSchema(videoTable);

export type Video = z.infer<typeof VideoSchema>;

export const PostSchema = createSelectSchema(postTable, {
  content: z.any(),
  photos: z.union([z.array(PhotoSchema), z.null()]).optional(),
});

export type Post = z.infer<typeof PostSchema>;

export type LogBest = typeof logBestTable.$inferSelect;

export type Recording = typeof recordingTable.$inferSelect;
export type RecordingInsert = typeof recordingTable.$inferInsert;

export type Log = typeof logTable.$inferSelect;

export type User = typeof userTable.$inferSelect;

export type Profile = typeof profileTable.$inferSelect;
export type ProfileInsert = typeof profileTable.$inferInsert;

export const ProfileSchema = createSelectSchema(profileTable, {
  overviewContent: z.any(),
  collegeOffersContent: z.any(),
  highlights: z.any(),
});

export const TeamSchema = createSelectSchema(teamTable);

export type Team = z.infer<typeof TeamSchema>;

export const TeamMemberSchema = createSelectSchema(teamMemberTable, {
  tags: z.array(z.string()),
}).extend({
  profile: ProfileSchema,
});

export type TeamMember = z.infer<typeof TeamMemberSchema>;

export const TeamMemberTypeSchema = z.enum(['athlete', 'staff']);

export type TeamMemberType = z.infer<typeof TeamMemberTypeSchema>;

export const TeamMemberExpandedSchema = TeamMemberSchema.extend({
  profile: ProfileSchema,
});

export type TeamMemberExpanded = z.infer<typeof TeamMemberExpandedSchema>;

export const TeamShareSchema = createSelectSchema(teamShareTable).extend({
  team: TeamSchema,
});

export type TeamShare = z.infer<typeof TeamShareSchema>;

export const ActivityTypeSchema = createSelectSchema(activityTypeTable);

export type ActivityType = z.infer<typeof ActivityTypeSchema>;

export const NutritionTypeSchema = createSelectSchema(nutritionTypeTable);

export type NutritionType = z.infer<typeof NutritionTypeSchema>;

export const NutritionSchema = createSelectSchema(nutritionTable);

export type Nutrition = z.infer<typeof NutritionSchema>;

export const NutritionExpandedSchema = NutritionSchema.extend({
  nutritionType: NutritionTypeSchema,
});

export type NutritionExpanded = z.infer<typeof NutritionExpandedSchema>;

export type LogWithActivityType = typeof logTable.$inferSelect & {
  activityType: ActivityType;
};

export const LogSchema = createSelectSchema(logTable);

export const LogWithActivityTypeSchema = LogSchema.extend({
  activityType: ActivityTypeSchema,
});

export const UserSchema = createSelectSchema(userTable);

export type UserType = z.infer<typeof UserSchema>;

export const FollowStateSchema = z.object({
  isFollowing: z.boolean().optional(),
  hasRequestedFollow: z.boolean().optional(),
});

export type FollowState = z.infer<typeof FollowStateSchema>;

export const ProfileWithUserSchema = ProfileSchema.extend({
  user: z.object({
    id: z.string(),
    name: z.string(),
  }),
});

export type ProfileWithUser = z.infer<typeof ProfileWithUserSchema>;

export const ProfileExpandedSchema = ProfileWithUserSchema.extend({
  followState: z.union([FollowStateSchema, z.null()]).optional(),
  isUserProfile: z.boolean().optional(),
});

export type ProfileExpanded = z.infer<typeof ProfileExpandedSchema>;

export const MessageSchema = createSelectSchema(messageTable);

export type Message = z.infer<typeof MessageSchema>;

export const MessageExpandedSchema = MessageSchema.extend({
  toUser: UserSchema,
  fromUser: UserSchema,
});

export type MessageExpanded = z.infer<typeof MessageExpandedSchema>;

// guides
export const GuideSchema = z.object({
  id: z.string(),
  nameId: z.string(),
  title: z.string(),
  html: z.string(),
  text: z.string(),
});

export type Guide = z.infer<typeof GuideSchema>;

// onboarding
export const OnboardingStepSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
});

export type OnboardingStep = z.infer<typeof OnboardingStepSchema>;

// health
export const HealthTypeSchema = z.enum([
  'steps',
  'distance-walking-running',
  'flights-climbed',
]);

export type HealthType = z.infer<typeof HealthTypeSchema>;

// highlight
export const HighlightSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
});

export type Highlight = z.infer<typeof HighlightSchema>;
